import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import FAQSchema from './SEO/FAQSchema';
import { Link } from 'react-router-dom';

const FAQ = ({ faqs = [] }) => {
  const [openIndex, setOpenIndex] = useState(null);

  // Default FAQs if none provided
  const defaultFAQs = [
    {
      question: "What services does Cyborg Sapient offer?",
      answer: "Cyborg Sapient offers comprehensive digital transformation solutions including Search Engine Optimization (SEO), Google Ads, Facebook Ads, YouTube Ads, SMS Marketing, Email Marketing, Content Marketing, WhatsApp Marketing, E-commerce Marketing, UI/UX Design, Social Media Development, and Software Development. We help businesses of all sizes achieve their digital marketing and technology goals."
    },
    {
      question: "How long does it take to see results from SEO services?",
      answer: "SEO is a long-term strategy, and results typically begin to show within 3-6 months. However, initial improvements in technical SEO and on-page optimization may be visible within 4-8 weeks. Significant organic traffic growth usually occurs within 6-12 months of consistent optimization. Our clients typically see a 40-150% increase in organic traffic within this timeframe."
    },
    {
      question: "What is the cost of digital marketing services?",
      answer: "Our digital marketing services are customized based on your business needs, goals, industry, and budget. We offer flexible pricing plans ranging from basic packages for small businesses to comprehensive enterprise solutions. Contact us for a free consultation, and we'll provide a detailed quote tailored to your specific requirements."
    },
    {
      question: "Do you work with businesses in specific industries?",
      answer: "Yes, we have expertise across multiple industries including Healthcare, Finance, E-commerce, Real Estate, Technology, Education, Retail, Hospitality, and more. Our team has experience delivering successful campaigns for clients across various sectors, and we customize our strategies to meet industry-specific requirements and regulations."
    },
    {
      question: "Can you help with both digital marketing and software development?",
      answer: "Absolutely! Cyborg Sapient is a full-service digital agency that offers both digital marketing services and software development. We can help you build custom web applications, mobile apps, and software solutions while also managing your digital marketing campaigns. This integrated approach ensures seamless alignment between your technology and marketing strategies."
    },
    {
      question: "How do you measure the success of digital marketing campaigns?",
      answer: "We use comprehensive analytics and reporting tools to track key performance indicators (KPIs) including website traffic, conversion rates, cost-per-acquisition (CPA), return on ad spend (ROAS), search engine rankings, engagement rates, and revenue growth. We provide regular monthly reports with detailed insights and recommendations for optimization."
    },
    {
      question: "What is your process for onboarding new clients?",
      answer: "Our onboarding process begins with a free consultation to understand your business goals and challenges. We then conduct a comprehensive audit of your current digital presence, develop a customized strategy, and present a detailed proposal. Once approved, we set up tracking tools, create optimized campaigns, and begin implementation with regular communication and reporting."
    },
    {
      question: "Do you provide ongoing support and maintenance?",
      answer: "Yes, we offer ongoing support and maintenance for all our services. This includes regular campaign optimization, performance monitoring, content updates, technical maintenance, and strategic adjustments based on performance data. We believe in building long-term partnerships with our clients to ensure sustained growth and success."
    }
  ];

  const displayFAQs = faqs.length > 0 ? faqs : defaultFAQs;

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <FAQSchema faqs={displayFAQs} />
      <section className="py-16 sm:py-20 px-6 sm:px-8 lg:px-12 bg-white min-h-screen flex items-center">
        <div className="max-w-4xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-gray-900 mb-4 tracking-tight">
              Frequently Asked<br />
              <span className="font-semibold">Questions</span>
            </h2>
            <p className="text-xl font-light text-gray-600">
              Get answers to common questions about our services
            </p>
          </motion.div>

          <div className="space-y-1">
            {displayFAQs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="border-b border-gray-200"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-0 py-6 text-left flex items-center justify-between hover:opacity-70 transition-opacity focus:outline-none"
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3 className="text-lg sm:text-xl font-medium text-gray-900 pr-8">
                    {faq.question}
                  </h3>
                  <div className="flex-shrink-0">
                    {openIndex === index ? (
                      <FaChevronUp className="text-gray-600" size={16} />
                    ) : (
                      <FaChevronDown className="text-gray-400" size={16} />
                    )}
                  </div>
                </button>
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      id={`faq-answer-${index}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="pb-6 pt-0">
                        <p className="text-gray-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-16 text-center"
          >
            <p className="text-lg font-light text-gray-600 mb-6">
              Still have questions? We're here to help!
            </p>
            <Link
              to="/contact"
              className="inline-block text-lg px-10 py-4 bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 text-white rounded-full font-medium hover:opacity-90 transition-opacity duration-200 shadow-lg"
            >
              Contact Us
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default FAQ;
